import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

/**
 * Global music-playback context. One <audio> element lives at the App root
 * and survives every route change — clicking a track on /library or /home
 * keeps it playing while the user browses to /watch/<id> or /admin.
 *
 * Shape: { track, playing, progress, duration, play, pause, toggle, stop, seek }
 *  track:   {id, title, artist, album, src, art, mirrors?} or null
 *  playing: boolean
 *  progress: seconds elapsed (number)
 *  duration: seconds total (number)
 */
const MusicContext = createContext(null);

export function MusicProvider({ children }) {
  const audioRef = useRef(null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // Initialise the singleton <audio> once. Browsers throttle multiple
  // simultaneous audio elements; we keep exactly one.
  useEffect(() => {
    if (audioRef.current) return;
    const a = new Audio();
    a.preload = "metadata";
    audioRef.current = a;
    const onTime = () => setProgress(a.currentTime || 0);
    const onLoaded = () => setDuration(a.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const play = useCallback((next) => {
    const a = audioRef.current;
    if (!a || !next?.src) return;
    // Same track? just resume.
    if (track && track.id === next.id && a.src) {
      a.play().catch(() => {});
      return;
    }
    a.src = next.src;
    setTrack(next);
    setProgress(0);
    a.play().catch((e) => console.warn("[music] play failed", e));
  }, [track]);

  const pause = useCallback(() => audioRef.current?.pause(), []);
  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }, []);
  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.src = "";
    }
    setTrack(null);
    setPlaying(false);
    setProgress(0);
    setDuration(0);
  }, []);
  const seek = useCallback((secs) => {
    const a = audioRef.current;
    if (a) a.currentTime = secs;
  }, []);

  return (
    <MusicContext.Provider value={{ track, playing, progress, duration, play, pause, toggle, stop, seek }}>
      {children}
    </MusicContext.Provider>
  );
}

export function useMusic() {
  const ctx = useContext(MusicContext);
  if (!ctx) throw new Error("useMusic must be used inside <MusicProvider>");
  return ctx;
}
